import { type DatabricksColumnDetail } from '@protoss/shared';
import {
  CheckIcon,
  IconSize,
  LightningBoltIcon,
  ResizableTable,
  Tooltip,
  type ResizableColumnDef,
} from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { useTableColumns } from '../hooks/useTableColumns';
import { dataCatalogApi } from '../services/dataCatalogApi';

interface DataCatalogColumnViewerProps {
  projectUuid: string;
  catalogName: string | null;
  schemaName: string | null;
  tableName: string | null;
  canEdit?: boolean;
}

type GenerateState = 'idle' | 'generating' | 'dispatched';

const DC_COLUMNS: ResizableColumnDef<DatabricksColumnDetail>[] = [
  {
    key: 'name',
    label: 'Name',
    defaultWidth: 200,
    minWidth: 80,
    maxWidth: 400,
    cellClassName: 'rt-table__col--mono',
    render: (col) => col.name,
  },
  {
    key: 'dataType',
    label: 'Type',
    defaultWidth: 150,
    minWidth: 80,
    maxWidth: 280,
    render: (col) => col.dataType,
  },
  {
    key: 'description',
    label: 'Description',
    defaultWidth: 420,
    minWidth: 120,
    maxWidth: 900,
    wrap: true,
    render: (col) => {
      if (col.aiDescription != null) {
        return (
          <>
            {col.aiDescription} <span className="dc-ai-badge">AI</span>
          </>
        );
      }
      if (col.description != null) return col.description;
      return <span className="ss-hint">-</span>;
    },
  },
];

export function DataCatalogColumnViewer({
  projectUuid,
  catalogName,
  schemaName,
  tableName,
  canEdit,
}: DataCatalogColumnViewerProps) {
  const { data, loading, error, page, pageSize, nextPage, prevPage, reload } = useTableColumns(
    projectUuid,
    catalogName,
    schemaName,
    tableName
  );
  const [generateState, setGenerateState] = React.useState<GenerateState>('idle');

  async function handleGenerateDescriptions() {
    if (!catalogName || !schemaName || !tableName) return;
    setGenerateState('generating');
    try {
      await dataCatalogApi.generateColumnDescriptions(
        projectUuid,
        catalogName,
        schemaName,
        tableName
      );
      setGenerateState('dispatched');
      setTimeout(() => {
        setGenerateState('idle');
      }, 2500);
    } catch {
      setGenerateState('idle');
    }
  }

  if (!catalogName || !schemaName || !tableName) {
    return (
      <div className="dc-col-viewer">
        {/* Empty header - provides the border-bottom that bridges the divider tick */}
        <div className="ss-browser__viewer-header" />
        <div className="ss-browser-viewer__empty">Select a table to view its columns.</div>
      </div>
    );
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;
  const showPagination = !!data && data.total > pageSize;

  const generateBtnClass = [
    'ss-panel__action-icon',
    generateState === 'generating' && 'ss-panel__action-icon--spinning',
    generateState === 'dispatched' && 'ss-panel__action-icon--dispatched',
  ]
    .filter(Boolean)
    .join(' ');

  const generateBtnLabel =
    generateState === 'generating'
      ? 'Generating AI descriptions…'
      : generateState === 'dispatched'
        ? 'AI descriptions queued'
        : 'Generate AI descriptions';

  return (
    <div className="dc-col-viewer">
      <div className="ss-browser__viewer-header">
        <span className="ss-browser__viewer-title">
          {schemaName}.{tableName}
        </span>
        <div className="ss-browser__viewer-header-actions">
          {!loading && !error && data && (
            <span className="ss-browser__subtitle">{data.total} columns</span>
          )}
          {canEdit && tableName && (
            <Tooltip.Simple label={generateBtnLabel}>
              <button
                type="button"
                className={generateBtnClass}
                onClick={() => void handleGenerateDescriptions()}
                disabled={generateState !== 'idle'}
                aria-label={generateBtnLabel}
              >
                {generateState === 'dispatched' ? (
                  <CheckIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
                ) : (
                  <LightningBoltIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
                )}
              </button>
            </Tooltip.Simple>
          )}
        </div>
      </div>

      <div className="split-pane__content-area">
        {loading && <div className="ss-browser-viewer__empty">Loading columns…</div>}
        {!loading && error && <PageErrorState message="Failed to load columns." onRetry={reload} />}
        {!loading && !error && data?.columns.length === 0 && (
          <div className="ss-browser-viewer__empty">No columns found.</div>
        )}
        {!loading && !error && data && data.columns.length > 0 && (
          <ResizableTable
            columns={DC_COLUMNS}
            rows={data.columns}
            getRowKey={(col) => col.name}
            aria-label={`Columns of ${schemaName}.${tableName}`}
          />
        )}
      </div>

      {showPagination && (
        <div className="split-pane__pagination">
          <span className="split-pane__pag-label">
            {data!.total} columns · Page {page} of {totalPages}
          </span>
          <div className="split-pane__pag-controls">
            <button
              type="button"
              className="split-pane__pag-btn"
              onClick={prevPage}
              disabled={page === 1}
              aria-label="Previous page"
            >
              Prev
            </button>
            <button
              type="button"
              className="split-pane__pag-btn"
              onClick={nextPage}
              disabled={page >= totalPages}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
